import { useEffect, useState, useCallback, useRef } from "react";

export interface AttentionItem {
  card_id: string;
  kind: "timesheet" | "expense";
  filename: string;
  source_page_index: number | null;
}

export interface RotationFailure extends AttentionItem {
  attempts: unknown[];
}

export interface AssignmentMissing extends AttentionItem {
  suggestions: Array<{ id: string; name: string; score: number }>;
  employee_raw: string | null;
}

export interface RetryStuck {
  card_id: string;
  job_id: string;
  retry_count: number;
  last_error: string | null;
}

export interface ConflictItem extends AttentionItem {
  state: string;
  first_name: string | null;
  last_name: string | null;
}

export interface AttentionSnapshot {
  rotation_failures: RotationFailure[];
  classification_unclear: AttentionItem[];
  assignment_missing: AssignmentMissing[];
  ocr_quality_low: Array<AttentionItem & { score: number; threshold: number }>;
  retry_stuck: RetryStuck[];
  pending_conflict: ConflictItem[];
  batch_ready_greenlight: { count: number; ids: string[] };
  in_progress: { ocr_pending: number; ocr_running: number };
  done: { validated: number; calculated: number; exported: number };
  is_empty: boolean;
  months_with_work: Array<{ month: string; open_count: number }>;
}

const EMPTY: AttentionSnapshot = {
  rotation_failures: [],
  classification_unclear: [],
  assignment_missing: [],
  ocr_quality_low: [],
  retry_stuck: [],
  pending_conflict: [],
  batch_ready_greenlight: { count: 0, ids: [] },
  in_progress: { ocr_pending: 0, ocr_running: 0 },
  done: { validated: 0, calculated: 0, exported: 0 },
  is_empty: true,
  months_with_work: [],
};

export function useAttentionPoll(month: string, clientId: string | null, intervalMs = 5000) {
  const [data, setData] = useState<AttentionSnapshot>(EMPTY);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const fetchOnce = useCallback(async () => {
    if (!month || !clientId) return;
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setLoading(true);
    try {
      const url = new URL("api/attention", document.baseURI);
      url.searchParams.set("month", month);
      url.searchParams.set("client_id", clientId);
      const res = await fetch(url.toString(), { credentials: "same-origin", signal: ctrl.signal });
      if (!res.ok) throw new Error(`${res.status}`);
      const json = (await res.json()) as AttentionSnapshot;
      setData(json);
      setError(null);
    } catch (err: any) {
      if (err.name === "AbortError") return;
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [month, clientId]);

  useEffect(() => {
    fetchOnce();
    const paused = () => document.visibilityState === "hidden";
    const tick = () => { if (!paused()) fetchOnce(); };
    const id = setInterval(tick, intervalMs);
    const onVisChange = () => { if (!paused()) fetchOnce(); };
    document.addEventListener("visibilitychange", onVisChange);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVisChange);
      abortRef.current?.abort();
    };
  }, [fetchOnce, intervalMs]);

  return { data, loading, error, refresh: fetchOnce };
}
